/**
 * UK Region/PMA Coordinates for Map Visualization
 * Maps region names to lat/lng coordinates for Leaflet markers
 */

export interface RegionCoordinate {
  lat: number;
  lng: number;
  displayName: string;
}

// Primary Market Areas (PMAs) - City center coordinates
export const PMA_COORDINATES: Record<string, RegionCoordinate> = {
  // Current PMAs
  "North London": { lat: 51.5614, lng: -0.1027, displayName: "North London" },
  "South London": { lat: 51.4526, lng: -0.1096, displayName: "South London" },
  "Birmingham": { lat: 52.4862, lng: -1.8904, displayName: "Birmingham" },
  "Manchester": { lat: 53.4808, lng: -2.2426, displayName: "Manchester" },
  "Leeds": { lat: 53.8008, lng: -1.5491, displayName: "Leeds" },
  "Glasgow": { lat: 55.8642, lng: -4.2518, displayName: "Glasgow" },
  "Bristol": { lat: 51.4545, lng: -2.5879, displayName: "Bristol" },
  "Edinburgh": { lat: 55.9533, lng: -3.1883, displayName: "Edinburgh" },
  "Liverpool": { lat: 53.4084, lng: -2.9916, displayName: "Liverpool" },
  "Sheffield": { lat: 53.3811, lng: -1.4701, displayName: "Sheffield" },
  
  // Additional UK cities for future expansion
  "Newcastle": { lat: 54.9783, lng: -1.6178, displayName: "Newcastle" },
  "Nottingham": { lat: 52.9548, lng: -1.1581, displayName: "Nottingham" },
  "Leicester": { lat: 52.6369, lng: -1.1398, displayName: "Leicester" },
  "Coventry": { lat: 52.4068, lng: -1.5197, displayName: "Coventry" },
  "Cardiff": { lat: 51.4816, lng: -3.1791, displayName: "Cardiff" },
  "Belfast": { lat: 54.5973, lng: -5.9301, displayName: "Belfast" },
  "Southampton": { lat: 50.9097, lng: -1.4044, displayName: "Southampton" },
  "Brighton": { lat: 50.8225, lng: -0.1372, displayName: "Brighton" },
  "Plymouth": { lat: 50.3755, lng: -4.1427, displayName: "Plymouth" },
  "Cambridge": { lat: 52.2053, lng: 0.1218, displayName: "Cambridge" },
  "Oxford": { lat: 51.7520, lng: -1.2577, displayName: "Oxford" },
  "Norwich": { lat: 52.6309, lng: 1.2974, displayName: "Norwich" },
  "Aberdeen": { lat: 57.1497, lng: -2.0943, displayName: "Aberdeen" },
  "Dundee": { lat: 56.4620, lng: -2.9707, displayName: "Dundee" },
  "Swansea": { lat: 51.6214, lng: -3.9436, displayName: "Swansea" },
};

// UK map center and default zoom
export const UK_MAP_CENTER: [number, number] = [54.5, -2.5];
export const UK_MAP_DEFAULT_ZOOM = 6;

// London-specific zoom (for when all regions are in London area)
export const LONDON_MAP_CENTER: [number, number] = [51.5074, -0.1278];
export const LONDON_MAP_ZOOM = 10;

/**
 * Get coordinates for a region name (case-insensitive matching)
 */
export function getRegionCoordinates(regionName: string): RegionCoordinate | null {
  // Try exact match first
  if (PMA_COORDINATES[regionName]) {
    return PMA_COORDINATES[regionName];
  }
  
  // Try case-insensitive match
  const lowerName = regionName.toLowerCase();
  for (const [key, coords] of Object.entries(PMA_COORDINATES)) {
    if (key.toLowerCase() === lowerName) {
      return coords;
    }
  }
  
  // Try partial match (e.g., "North London PMA" should match "North London")
  for (const [key, coords] of Object.entries(PMA_COORDINATES)) {
    if (lowerName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerName)) {
      return coords;
    }
  }
  
  return null;
}

/**
 * Calculate map bounds to fit all regions
 */
export function calculateMapBounds(regions: string[]): {
  center: [number, number];
  zoom: number;
} {
  const coords = regions
    .map(r => getRegionCoordinates(r))
    .filter((c): c is RegionCoordinate => c !== null);
  
  if (coords.length === 0) {
    return { center: UK_MAP_CENTER, zoom: UK_MAP_DEFAULT_ZOOM };
  }
  
  if (coords.length === 1) {
    return { center: [coords[0].lat, coords[0].lng], zoom: 10 };
  }
  
  // Calculate bounding box
  const lats = coords.map(c => c.lat);
  const lngs = coords.map(c => c.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  
  // Center point
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  
  // Calculate appropriate zoom based on spread
  const latSpread = maxLat - minLat;
  const lngSpread = maxLng - minLng;
  const maxSpread = Math.max(latSpread, lngSpread);
  
  let zoom = UK_MAP_DEFAULT_ZOOM;
  if (maxSpread < 1) zoom = 9;
  else if (maxSpread < 2) zoom = 8;
  else if (maxSpread < 4) zoom = 7;
  else zoom = 6;
  
  return { center: [centerLat, centerLng], zoom };
}

/**
 * Color scale utilities
 */
export type ColorScaleType = 'performance' | 'sequential' | 'diverging';

/**
 * Get color based on value within a range
 * Performance scale: Green (best) → Yellow → Red (worst)
 */
export function getPerformanceColor(value: number, min: number, max: number): string {
  if (max === min) return '#eab308'; // Yellow if all same
  
  const ratio = (value - min) / (max - min);
  
  // Green (#22c55e) → Yellow (#eab308) → Red (#ef4444)
  if (ratio >= 0.66) {
    // Top third - green gradient
    const t = (ratio - 0.66) / 0.34;
    return interpolateColor('#eab308', '#22c55e', t);
  } else if (ratio >= 0.33) {
    // Middle third - yellow gradient
    const t = (ratio - 0.33) / 0.33;
    return interpolateColor('#ef4444', '#eab308', t);
  } else {
    // Bottom third - red gradient
    const t = ratio / 0.33;
    return interpolateColor('#dc2626', '#ef4444', t);
  }
}

/**
 * Get color for sequential scale (light to dark blue)
 */
export function getSequentialColor(value: number, min: number, max: number): string {
  if (max === min) return '#3b82f6';
  const ratio = (value - min) / (max - min);
  return interpolateColor('#dbeafe', '#1d4ed8', ratio);
}

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, ratio: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);
  
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

